import { ChangeDetectionStrategy, Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { LanguageService } from './core/services/language.service';
import { ThemeService } from './core/services/theme.service';
import { AccountSettingsService } from './core/services/account-settings.service';
import { IonicModule } from '@ionic/angular';
import { ShellHeaderComponent } from './core/components/shell-header/shell-header.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-root',
    // Shell is hoisted here (once) instead of being re-declared per page: previously every
    // routed page inlined its own <app-shell-header>, so Ionic's default page-transition
    // animation on ion-router-outlet re-mounted+re-animated the ENTIRE page — header/nav
    // included — on every navigation, reading as a full reload with a visible scroll jump.
    // Hoisting the header outside the outlet means only the inner content region remounts.
    // [animated]="false" additionally removes Ionic's translateY/opacity page transition,
    // which is not needed for this desktop developer-portal shell and was the direct
    // mechanism of the "jumps down, settles back up" jitter.
    // Note: the shell header renders a real <ion-header>/<ion-toolbar>. When it lived inside
    // each page (sibling of that page's own <ion-content>, both inside the same .ion-page),
    // Ionic's internal flex layout (.ion-page { display:flex; flex-direction:column }) sized
    // ion-content automatically to the remaining space. Now that the header lives OUTSIDE
    // ion-router-outlet, each routed page's .ion-page contains only its own <ion-content> and
    // gets absolutely positioned to fill its nearest positioned ancestor — with no header
    // inside it, ion-content renders from the very top and slides under the external header.
    // Wrapping the outlet in a `position: relative; flex: 1` container (with ion-app itself as
    // the flex column) gives .ion-page a correctly-sized, correctly-positioned parent instead:
    // the header keeps its natural height, the wrapper gets exactly what's left.
    template: `
    <ion-app>
      <div class="app-shell">
        @if (titleKey$ | async; as titleKey) {
          <app-shell-header [title]="titleKey | translate"></app-shell-header>
        }
        <div class="app-shell__outlet">
          <ion-router-outlet [animated]="false"></ion-router-outlet>
        </div>
      </div>
    </ion-app>
  `,
    styles: [`
    .app-shell {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .app-shell__outlet {
      position: relative;
      flex: 1;
      min-height: 0;
    }
  `],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        ShellHeaderComponent,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class AppComponent {
  // Emits the active leaf route's `data.titleKey` on every completed navigation. Routes
  // that don't set titleKey (access, callback, unauthorized) emit '' so the @if above
  // hides the header on those pages, preserving current per-route visibility.
  readonly titleKey$: Observable<string> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map(() => {
      let route = this.router.routerState.root;
      while (route.firstChild) {
        route = route.firstChild;
      }
      return route.snapshot.data['titleKey'] ?? '';
    }),
  );

  constructor(
    private router: Router,
    private theme: ThemeService,
    private language: LanguageService,
    private accountSettings: AccountSettingsService,
  ) {
    this.theme.init();
    this.language.initLanguage();
    this.accountSettings.start();
  }
}
